import React, { useState } from 'react';
import { ArrowRight16, Laptop16, Video16 } from '@carbon/icons-react';
import { CtasButton, EComm, FigmaConfig } from '../types/types';
import { Button, ButtonSet, Select, SelectItem } from 'carbon-components-react';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import Cookies from 'js-cookie';
import { segmentTracking } from '../types/types';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { Helmet } from 'react-helmet';
import { FormModal } from 'src/common/utils/FormModal';
import Thanks from './Thanks';
import { shortcode } from '../FigmaDemo.widget';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';

interface DemoCtasProps {
  ctas: CtasButton[]; // Array of CtasButton objects
  urxlang: string;
  env: string;
  demotitle: string;
  producttitle: string;
  toggleButtons?: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    action: string,
  ) => void | undefined;
  visibleDiv?: string;
  smallScreenMessage: string;
  ddoCountry: string;
  watch_video?: string;
  explore_demo?: string;
  configObj: FigmaConfig;
  onVideoSelect?: (videoId: string) => void;
}

// declare const adobeDataLayer: {
//   push?: (...args: any[]) => void;
// };

const DemoCtas: React.FC<DemoCtasProps> = (props) => {
  const companyId = '';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lang = props.urxlang;
  const localeCodeForEcomm = swapCountryAndLanguage(
    props.urxlang as IBMLocale,
  ).replaceAll('-', '/');
  const [closeModal, setCloseModal] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [formId, setFormId] = useState('');
  const cookieURL = useCookiesURL();
  const pageUrl = window.location.pathname;
  // const canPushToAdobeDataLayer =
  //   typeof adobeDataLayer !== 'undefined' &&
  //   typeof adobeDataLayer.push === 'function';

  //this is for the thank you modals
  const [openThankYou, setOpenThankYou] = useState(false);
  const [enableThankYou, setEnableThankYou] = useState(false);

  //we need to set the values for the form modal here
  const [thanksModalHeading, setThanksModalHeading] = useState('');
  const [thanksModalBody, setThanksModalBody] = useState('');
  const [thanksModalUrl, setThanksModalUrl] = useState('');
  const [thanksModalUrlLabel, setThanksModalUrlLabel] = useState('');
  const [thanksCloseModal, setThanksCloseModal] = useState('');

  window.urxEnvironment = props.env;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
    setCloseModal(true);

    segmentTracking('CTA Clicked', {
      productTitle: 'DIAC Demo - ' + props.producttitle,
      pageTitle: 'DIAC Demo - ' + props.demotitle,
      object: 'DIAC Demo - URX Form - ' + props.demotitle,
      resultValue: 'CTA clicked',
      CTA: 'Form submitted',
      location: 'DIAC-Demo',
      action: 'Clicked',
      field: '',
    });

    // if (canPushToAdobeDataLayer) {
    //   console.log('Calling adobeDataLayer: formComplete - ' + formId);
    // }

    if (enableThankYou) {
      setOpenThankYou(true);
      setEnableThankYou(false);
    }
  };

  const setCookie = (formId?: string) => {
    let useThisForm = null;
    if (formId) {
      useThisForm = formId;
    }
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: useThisForm,
        // One field to provide all values (stringify)
        // adding this for no reason, to trigger an update
        dcq: {
          Q_NOTESDATA: cookieURL + ' Demo Title: ' + props.demotitle,
        },
      }),
    );
  };

  const getEcommStatus = (
    countryCode: string,
    ecomm: EComm | undefined,
  ): 'enable' | 'disable' =>
    ecomm?.enabled?.includes(countryCode.toLowerCase())
      ? 'enable'
      : ecomm?.disable?.includes(countryCode.toLowerCase())
      ? 'disable'
      : 'enable';

  const handleVideoSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedVideoId = event.target.value;
    if (selectedVideoId) {
      const selectedVideo = props.configObj.videos?.find(
        (video) => video.id === selectedVideoId,
      );
      segmentTracking('CTA Clicked', {
        productTitle: 'DIAC Demo - ' + props.producttitle,
        pageTitle: 'DIAC Demo - ' + props.demotitle,
        object: 'DIAC Demo - ' + props.demotitle,
        resultValue: 'CTA clicked',
        CTA: selectedVideo
          ? `Watched video ${selectedVideo.name}`
          : 'Watched video',
        location: 'DIAC-Demo',
        action: 'Clicked',
        field: '',
      });
      if (props.onVideoSelect) {
        props.onVideoSelect(selectedVideoId);
      }
      if (props.toggleButtons) {
        // Create a synthetic event for the toggleButtons function
        const syntheticEvent = {
          preventDefault: () => {
            // Prevent default behavior
            return;
          },
        } as React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>;
        props.toggleButtons(syntheticEvent, 'video');
      }
    }
  };

  return (
    <>
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      <div>
        <Thanks
          openThanks={openThankYou}
          heading={thanksModalHeading}
          body_text={thanksModalBody}
          url={thanksModalUrl}
          url_label={thanksModalUrlLabel}
          close_modal={thanksCloseModal}
          setShowThanksModal={setOpenThankYou}
        ></Thanks>
        <ButtonSet>
          {props.ctas.map((cta, index) =>
            cta.url ? (
              getEcommStatus(props.ddoCountry, cta.ecomm) === 'enable' ? (
                // Render the button for cta.url
                <Button
                  id={`figma_demo_${index}`}
                  key={index}
                  iconDescription="Icon Description"
                  renderIcon={ArrowRight16}
                  kind={index === 0 ? 'primary' : 'tertiary'}
                  href={
                    cta.ecomm?.enabled?.includes(props.ddoCountry)
                      ? cta.url_ecomm?.replaceAll(
                          'localecode',
                          localeCodeForEcomm,
                        )
                      : cta.url
                  }
                  onClick={() => {
                    // window.open(cta.url, '_blank');
                    segmentTracking('CTA Clicked', {
                      productTitle: 'DIAC Demo - ' + props.producttitle,
                      pageTitle: 'DIAC Demo - ' + props.demotitle,
                      object: 'DIAC Demo - ' + props.demotitle,
                      resultValue: 'CTA clicked',
                      CTA: cta.title,
                      location: 'DIAC-Demo',
                      action: 'Clicked',
                      field: '',
                    });
                  }}
                >
                  {cta.title}
                </Button>
              ) : getEcommStatus(props.ddoCountry, cta.ecomm) === 'disable' &&
                cta.disable_title ? (
                <Button
                  key={index}
                  id={`figma_demo_${index}`}
                  renderIcon={ArrowRight16}
                  kind={index === 0 ? 'primary' : 'tertiary'}
                  onClick={() => {
                    setFormId(cta.disable_urx || '');
                    initUrxForm(
                      props.env,
                      'urx-form-' + formId,
                      cta.disable_urx || '',
                      (cta.disable_urx_lang || 'us-en') as IBMLocale,
                      companyId,
                    );
                    setDrawerOpen(true);
                    setCookie(cta.urx);
                    segmentTracking('CTA Clicked', {
                      productTitle: 'DIAC Demo - ' + props.producttitle,
                      pageTitle: 'DIAC Demo - ' + props.demotitle,
                      object: 'DIAC Demo - ' + props.demotitle,
                      resultValue: 'CTA clicked',
                      CTA: cta.disable_title ? cta.disable_title : '',
                      location: 'DIAC-Demo',
                      action: 'Clicked',
                      field: '',
                    });
                  }}
                >
                  {cta.disable_title}
                </Button>
              ) : null
            ) : cta.urx ? (
              // Render the button for cta.urx
              <Button
                key={index}
                id={`figma_demo_${index}`}
                renderIcon={ArrowRight16}
                kind={index === 0 ? 'primary' : 'tertiary'}
                onClick={() => {
                  setFormId(cta.urx || '');
                  // console.log('jamie', (cta.urx_lang || 'us-en') as IBMLocale);
                  initUrxForm(
                    props.env,
                    'urx-form-' + formId,
                    cta.urx || '',
                    (cta.urx_lang || 'us-en') as IBMLocale,
                    companyId,
                  );
                  // if (canPushToAdobeDataLayer) {
                  //   console.log(
                  //     'Calling adobeDataLayer: formLoad - ' + cta.urx,
                  //   );
                  //   console.log(
                  //     'Calling adobeDataLayer: formStart - ' + cta.urx,
                  //   );
                  // }
                  if (cta.thanks) {
                    setThanksModalHeading(cta.thanks.heading);
                    setThanksModalBody(cta.thanks.body);
                    setThanksCloseModal(cta.thanks.close_modal);
                    if (cta.thanks.url) {
                      setThanksModalUrl(cta.thanks.url);
                    }
                    if (cta.thanks.url_label) {
                      setThanksModalUrlLabel(cta.thanks.url_label);
                    }
                    setEnableThankYou(true);
                  }
                  setDrawerOpen(true);
                  setCookie(cta.urx);
                  segmentTracking('CTA Clicked', {
                    productTitle: 'DIAC Demo - ' + props.producttitle,
                    pageTitle: 'DIAC Demo - ' + props.demotitle,
                    object: 'DIAC Demo - ' + props.demotitle,
                    resultValue: 'CTA clicked',
                    CTA: cta.title,
                    location: 'DIAC-Demo',
                    action: 'Clicked',
                    field: '',
                  });
                }}
              >
                {cta.title}
              </Button>
            ) : // Render nothing if neither cta.url nor cta.urx is present
            null,
          )}
          {props.toggleButtons && props.visibleDiv ? (
            <>
              {props.visibleDiv === 'demo' ? (
                props.configObj.videos && props.configObj.videos.length > 1 ? (
                  <Select
                    id="video-select"
                    labelText=""
                    onChange={handleVideoSelect}
                    defaultValue=""
                    className="video-select-auto-width"
                  >
                    <SelectItem
                      key="placeholder"
                      value=""
                      text="Watch video"
                      disabled
                    />
                    {props.configObj.videos.map((video) => (
                      <SelectItem
                        key={video.id}
                        value={video.id}
                        text={video.name}
                      />
                    ))}
                  </Select>
                ) : (
                  <Button
                    key={5}
                    renderIcon={Video16}
                    kind={'tertiary'}
                    onClick={(event) => {
                      props.toggleButtons?.(event, 'video');
                      segmentTracking('CTA Clicked', {
                        productTitle: 'DIAC Demo - ' + props.producttitle,
                        pageTitle: 'DIAC Demo - ' + props.demotitle,
                        object: 'DIAC Demo - ' + props.demotitle,
                        resultValue: 'CTA clicked',
                        CTA: 'Watch video button',
                        location: 'DIAC-Demo',
                        action: 'Clicked',
                        field: '',
                      });
                    }}
                  >
                    {props.watch_video}
                  </Button>
                )
              ) : null}
              {props.visibleDiv === 'video' ? (
                <Button
                  key={6}
                  renderIcon={Laptop16}
                  kind={'tertiary'}
                  onClick={(event) => {
                    props.toggleButtons?.(event, 'demo');
                    segmentTracking('CTA Clicked', {
                      productTitle: 'DIAC Demo - ' + props.producttitle,
                      pageTitle: 'DIAC Demo - ' + props.demotitle,
                      object: 'DIAC Demo - ' + props.demotitle,
                      resultValue: 'CTA clicked',
                      CTA: 'Explore demo button',
                      location: 'DIAC-Demo',
                      action: 'Clicked',
                      field: '',
                    });
                  }}
                >
                  {props.explore_demo}
                </Button>
              ) : null}
            </>
          ) : null}
        </ButtonSet>
        {props.toggleButtons &&
        props.visibleDiv &&
        props.visibleDiv === 'video' ? (
          <p className="small_screen_note">{props.smallScreenMessage}</p>
        ) : null}
      </div>
      <FormModal
        pageTitle={props.demotitle}
        object="Figma Demo"
        productTitle={props.producttitle}
        pageUrl={pageUrl}
        location={'Figma Demo Widget'}
        bpName={''}
        logo={''}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formId={formId}
        lang={lang}
        id={'urx-form-' + formId}
        closeModal={closeModal}
        setCloseModal={setCloseModal}
      ></FormModal>
    </>
  );
};

export default DemoCtas;
